import React, { memo } from 'react';
import { View, Pressable, ScrollView, Image, Platform } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { getPlatformDisplayName, getPlatformLogo } from './utils/platformFilterUtils';

interface PlatformFilterChipsProps {
  platforms: string[];
  selectedPlatforms: string[];
  onPlatformToggle: (platform: string) => void;
  integrationData?: any[];
  theme?: boolean;
}

const PlatformFilterChips: React.FC<PlatformFilterChipsProps> = memo(({
  platforms,
  selectedPlatforms,
  onPlatformToggle,
  integrationData,
  theme: themeProp,
}) => {
  const theme = themeProp !== undefined ? themeProp : themeSelector();

  if (!platforms || platforms.length === 0) {
    return null;
  }

  return (
    <View style={{
      paddingVertical: 12,
      paddingHorizontal: 20,
      backgroundColor: theme ? '#f8fafc' : '#0f172a',
      borderBottomWidth: 1,
      borderBottomColor: theme ? '#e2e8f0' : '#334155',
    }}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ gap: 8, paddingRight: 20 }}
      >
        {/* "All Platforms" option */}
        <Pressable
          onPress={() => {
            // Clear all selections to show all posts
            platforms.forEach(platform => {
              if (selectedPlatforms.includes(platform)) {
                onPlatformToggle(platform);
              }
            });
          }}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: 16,
            paddingVertical: 8,
            borderRadius: 20,
            backgroundColor: selectedPlatforms.length === 0
              ? (theme ? '#3b82f6' : '#2563eb')
              : (theme ? '#ffffff' : '#1e293b'),
            borderWidth: 1,
            borderColor: selectedPlatforms.length === 0
              ? (theme ? '#3b82f6' : '#2563eb')
              : (theme ? '#e2e8f0' : '#475569'),
            minHeight: 36,
          }}
        >
          <Text
            style={{
              fontSize: 13,
              fontWeight: '600',
              color: selectedPlatforms.length === 0
                ? '#ffffff'
                : (theme ? '#1e293b' : '#f1f5f9'),
            }}
          >
            All Platforms
          </Text>
        </Pressable>

        {/* Individual platform chips */}
        {platforms.map((platform) => {
          const isSelected = selectedPlatforms.includes(platform);
          const platformLogo = getPlatformLogo(platform, integrationData);
          const displayName = getPlatformDisplayName(platform);

          return (
            <Pressable
              key={platform}
              onPress={() => onPlatformToggle(platform)}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingHorizontal: 12,
                paddingVertical: 8,
                borderRadius: 20,
                backgroundColor: isSelected
                  ? (theme ? '#3b82f6' : '#2563eb')
                  : (theme ? '#ffffff' : '#1e293b'),
                borderWidth: 1,
                borderColor: isSelected
                  ? (theme ? '#3b82f6' : '#2563eb')
                  : (theme ? '#e2e8f0' : '#475569'),
                minHeight: 36,
                gap: 6,
              }}
            >
              {platformLogo ? (
                <Image
                  source={{ uri: platformLogo }}
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                  }}
                  resizeMode="contain"
                />
              ) : (
                <MaterialIcons
                  name="link"
                  size={16}
                  color={isSelected ? '#ffffff' : (theme ? '#64748b' : '#94a3b8')}
                />
              )}
              <Text
                style={{
                  fontSize: 13,
                  fontWeight: '600',
                  color: isSelected
                    ? '#ffffff'
                    : (theme ? '#1e293b' : '#f1f5f9'),
                }}
              >
                {displayName}
              </Text>
              {isSelected && (
                <MaterialIcons
                  name="check-circle"
                  size={16}
                  color="#ffffff"
                />
              )}
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
});

PlatformFilterChips.displayName = 'PlatformFilterChips';

export default PlatformFilterChips;

